import { useEffect, useRef, useState } from "react";
import { prefersReducedMotion } from "@/lib/gsapSetup";

/**
 * Reusable, performant media element.
 *
 * Props:
 *  - poster:  image URL shown first and used as graceful fallback
 *  - sources: [{ src, type, kind }] ordered <source> list (local | cdn | external)
 *  - alt:     accessible description
 *  - className: applied to the media (img/video) element
 *
 * Behaviour:
 *  - Lazy-loads: the <video> is only mounted when the frame nears the viewport.
 *  - Autoplays muted + looping + playsInline once it can play.
 *  - Pauses automatically when scrolled off-screen (saves CPU/GPU + battery).
 *  - Falls back to the poster (with a slow Ken Burns pan) when there are no
 *    sources, on load error, or when the user prefers reduced motion.
 */
export default function Media({ poster, sources = [], alt = "", className = "" }) {
  const wrapRef = useRef(null);
  const videoRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [canPlay, setCanPlay] = useState(false);
  const [failed, setFailed] = useState(false);

  const reduced = typeof window !== "undefined" && prefersReducedMotion();
  const hasVideo = sources.length > 0 && !reduced && !failed;

  // Lazy mount + play/pause based on visibility
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
        const v = videoRef.current;
        if (!v) return;
        if (entry.isIntersecting) {
          const p = v.play();
          if (p && p.catch) p.catch(() => {});
        } else {
          v.pause();
        }
      },
      { rootMargin: "200px 0px", threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasVideo]);

  return (
    <div ref={wrapRef} className="absolute inset-0 h-full w-full">
      {/* Poster layer — always present, fades out when video is ready */}
      <img
        src={poster}
        alt={alt}
        width="1280"
        height="720"
        loading="lazy"
        decoding="async"
        className={`${className} ${!hasVideo ? "ken-burns" : ""} absolute inset-0 transition-opacity duration-700`}
        style={{ opacity: hasVideo && canPlay ? 0 : 1 }}
      />

      {hasVideo && inView && (
        <video
          ref={videoRef}
          className={`${className} absolute inset-0 transition-opacity duration-700`}
          style={{ opacity: canPlay ? 1 : 0 }}
          poster={poster}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
          onCanPlay={() => setCanPlay(true)}
          onError={() => {
            if (process.env.NODE_ENV !== "production") {
              // eslint-disable-next-line no-console
              console.warn("[Media] no playable source, falling back to poster:", poster);
            }
            setFailed(true);
          }}
        >
          {sources.map((s) => (
            <source key={s.src} src={s.src} type={s.type} />
          ))}
        </video>
      )}
    </div>
  );
}
